import React, { useEffect, useRef, useState } from "react";
import { Field, FormikProvider, useFormik } from "formik";
import moment from "moment";
import { Spin } from "antd";
import { Col, OverlayTrigger, Popover, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import CustomFlatpickr from "../../../CustomControls/CustomFlatpickr";
import CustomPaginationButtons from "../../../CustomControls/CustomPaginationButtons";
import CustomSizePerPage from "../../../CustomControls/CustomSizePerPage";
import { post } from "../../../helpers/api_helper";
import {
  POST_GetRawDataDashboardDetail,
  POST_GetRawDataDashboardSummary,
  POST_RawDataDashboardDetailExport,
} from "../../../helpers/url_helper";
import { errorMessage } from "../CommonFunctions";
import parameterText from "./TestingRawDataParameterText";

// Cards that follow the From / To date range (entries created by the logged-in user)
const PERIOD_CARDS = [
  { type: 1, key: "ReadingsLogged", label: "Raw Data Readings Logged", icon: "la la-flask", tone: "primary" },
  { type: 2, key: "TCsWithRawData", label: "TC Nos With Raw Data", icon: "la la-clipboard-list", tone: "primary" },
  { type: 3, key: "EquipmentUsageEntries", label: "Equipment Usage Entries", icon: "la la-cogs", tone: "primary" },
];

// Cards that always show the current position of the allocated tests
const POSITION_CARDS = [
  { type: 4, key: "TCsRawDataStatus", label: "Allocated TC Nos Raw Data Status", icon: "la la-exclamation-circle", tone: "warning" },
  { type: 5, key: "ParametersTotal", label: "Allocated Test Parameters", icon: "la la-list", tone: "primary" },
  { type: 6, key: "ParametersDone", label: "Parameters - Result Entered", icon: "la la-check-circle", tone: "success" },
  { type: 7, key: "ParametersPending", label: "Parameters - Result Entry Pending", icon: "la la-clock", tone: "danger" },
  { type: 8, key: "ParametersNoEquipment", label: "Parameters Without Equipment", icon: "la la-unlink", tone: "warning" },
];

const ALL_CARDS = [...PERIOD_CARDS, ...POSITION_CARDS];

// Columns of the detail list, per card. Every column has a fixed width (% of the table, each list adds up to 100)
// so the layout never shifts with the data; long text wraps inside its column.
const DETAIL_COLUMNS = {
  1: [
    { title: "TC No", width: "14%", render: (r) => r.TCNo, bold: true },
    { title: "Test", width: "14%", render: (r) => r.TestName },
    { title: "Parameter", width: "22%", render: parameterText },
    { title: "Reading", width: "7%", render: (r) => r.ResultCount },
    { title: "Result Value", width: "12%", render: (r) => r.ResultValue },
    { title: "Equipment", width: "17%", render: (r) => r.MachineName },
    { title: "Entered On", width: "14%", render: (r) => r.EnteredOn },
  ],
  2: [
    { title: "TC No", width: "30%", render: (r) => r.TCNo, bold: true },
    { title: "Readings Logged", width: "30%", render: (r) => r.ReadingsDone },
    { title: "Last Entered On", width: "40%", render: (r) => r.EnteredOn },
  ],
  3: [
    { title: "TC No", width: "30%", render: (r) => r.TCNo, bold: true },
    { title: "Equipment", width: "40%", render: (r) => r.MachineName },
    { title: "Used On", width: "30%", render: (r) => r.EnteredOn },
  ],
  4: [
    { title: "TC No", width: "25%", render: (r) => r.TCNo, bold: true },
    { title: "Test", width: "50%", render: (r) => r.TestName },
    {
      title: "Status",
      width: "25%",
      render: (r) => (
        <span className={r.Status === "Done" ? "text-success" : r.Status === "Partial" ? "text-warning" : "text-danger"}>
          {r.Status}
        </span>
      ),
    },
  ],
};
DETAIL_COLUMNS[8] = [
  { title: "TC No", width: "20%", render: (r) => r.TCNo, bold: true },
  { title: "Test", width: "30%", render: (r) => r.TestName },
  { title: "Parameter", width: "50%", render: parameterText },
];
const PARAMETER_COLUMNS = [
  { title: "TC No", width: "14%", render: (r) => r.TCNo, bold: true },
  { title: "Test", width: "16%", render: (r) => r.TestName },
  { title: "Parameter", width: "24%", render: parameterText },
  { title: "Equipment", width: "24%", render: (r) => r.MachineName || "-" },
  { title: "Readings", width: "10%", render: (r) => `${r.ReadingsDone} / ${r.ReadingsExpected}` },
  {
    title: "Status",
    width: "12%",
    render: (r) => (
      <span className={r.Status === "Done" ? "text-success" : "text-danger"}>
        {r.Status}
      </span>
    ),
  },
];
[5, 6, 7].forEach((t) => (DETAIL_COLUMNS[t] = PARAMETER_COLUMNS));

const dateText = (value) => moment(value || new Date()).format("YYYY-MM-DD"); // sent to the API
const displayDate = (value) => moment(value || new Date()).format("D-MMM-YYYY"); // shown to the user, same as the date fields (j-M-Y)

const TestingRawDataDashboard = () => {
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [selectedType, setSelectedType] = useState(0);
  const [detailRows, setDetailRows] = useState([]);
  const [recordCount, setRecordCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(10);
  const latestDetailRef = useRef(0);

  // From / To date range, same control and format as the other screens (default: today)
  const formik = useFormik({
    initialValues: { FromDate: new Date(), ToDate: new Date() },
  });
  const { FromDate, ToDate } = formik.values;
  const fromDate = dateText(FromDate);
  const toDate = dateText(ToDate);

  const payload = (extra = {}) => ({
    FromDate: fromDate,
    ToDate: toDate,
    CompanyIDEncrypt: AuthUser?.CompanyIDEncrypt,
    BranchIDEncrypt: AuthUser?.BranchIDEncrypt,
    LoginIDEncrypt: AuthUser?.LoginIDEncrypt,
    ...extra,
  });

  const loadSummary = async () => {
    setSummaryLoading(true);
    await post(POST_GetRawDataDashboardSummary, payload())
      .then((response) => {
        if (response?.IsSuccess) {
          setSummary(response);
        } else {
          errorMessage(response?.Message);
        }
      })
      .catch((error) => console.log(error));
    setSummaryLoading(false);
  };

  const loadDetail = async (type, page = 1, sizePerPage = currentSizePerPage) => {
    const requestNo = ++latestDetailRef.current;
    setDetailLoading(true);
    await post(
      POST_GetRawDataDashboardDetail,
      payload({ DetailType: type, CurrentPage: page, PageSize: sizePerPage }),
    )
      .then((response) => {
        if (latestDetailRef.current !== requestNo) return; // another card was opened meanwhile
        if (response?.IsSuccess) {
          const rows = response?.ListOfDetail ?? [];
          if (page > 1 && rows.length === 0) {
            // the last row of the last page is gone (e.g. after a refresh): step back one page
            loadDetail(type, page - 1, sizePerPage);
            return;
          }
          setDetailRows(rows);
          setRecordCount(response?.TotalRecords ?? 0);
          setCurrentPage(page);
          setCurrentSizePerPage(sizePerPage);
        } else {
          setDetailRows([]);
          setRecordCount(0);
          errorMessage(response?.Message);
        }
      })
      .catch((error) => console.log(error));
    if (latestDetailRef.current === requestNo) setDetailLoading(false);
  };

  // the To date can never be before the From date
  useEffect(() => {
    if (ToDate && FromDate && ToDate < FromDate) {
      formik.setFieldValue("ToDate", FromDate);
    }
  }, [FromDate]);

  // reload the summary (and the open detail list) when the date range changes
  useEffect(() => {
    loadSummary();
    if (selectedType) loadDetail(selectedType, 1, currentSizePerPage);
  }, [fromDate, toDate]);

  // Excel export of the open detail list (all rows, same filters as the screen)
  const exportDetail = async () => {
    if (!selectedType) return;
    setDetailLoading(true);
    await post(POST_RawDataDashboardDetailExport, payload({ DetailType: selectedType }))
      .then((response) => {
        if (response?.IsSuccess) {
          window.open(response?.FilePath);
        } else {
          errorMessage(response?.Message);
        }
      })
      .catch((error) => console.log(error));
    setDetailLoading(false);
  };

  const handleRefresh = () => {
    loadSummary();
    if (selectedType) loadDetail(selectedType, currentPage, currentSizePerPage);
  };

  // page / page size chosen in the pager of the detail list
  const getData = (page, sizePerPage) => {
    if (selectedType) loadDetail(selectedType, page, sizePerPage);
  };

  const handleCardClick = (type, event) => {
    event?.currentTarget?.blur(); // no leftover focus / pressed look on the card
    if (selectedType === type) {
      latestDetailRef.current++; // drop a response that may still be on its way
      setSelectedType(0); // tapping the open card closes its detail list
      setDetailRows([]);
      setRecordCount(0);
      setDetailLoading(false);
      return;
    }
    setSelectedType(type);
    setDetailRows([]);
    setRecordCount(0);
    loadDetail(type, 1, currentSizePerPage);
  };

  const renderCard = (card) => {
    const isSelected = selectedType === card.type;
    return (
      <button
        type="button"
        key={card.type}
        aria-pressed={isSelected}
        className={`rdd-card rdd-${card.tone}${isSelected ? " rdd-selected" : ""}`}
        onClick={(e) => handleCardClick(card.type, e)}
      >
        <span className="d-flex align-items-center justify-content-between w-100">
          <span className="rdd-count">{summary ? summary[card.key] : "-"}</span>
          <i className={`${card.icon} fs-2`} />
        </span>
        <span className="rdd-label">{card.label}</span>
      </button>
    );
  };

  const selectedCard = ALL_CARDS.find((c) => c.type === selectedType);
  const columns = DETAIL_COLUMNS[selectedType] || [];

  return (
    <div className="card m-1 raw-data-dashboard">
      <div className="card-body p-1">
        <div className="tbl-caption p-2">
          <h4 className="heading mb-0">Testing Raw Data Dashboard</h4>
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm me-2"
              onClick={handleRefresh}
            >
              <i className="fa-solid fa-refresh me-2"></i>Refresh
            </button>
            <Link
              to="/testing-raw-data-log-book"
              className="btn btn-primary btn-sm"
            >
              <i className="fa-solid fa-pen-to-square me-2"></i>Raw Data Entry
            </Link>
          </div>
        </div>
        <hr className="mt-0 mb-1"></hr>

        <div className="p-2 pt-0 pb-0">
          <FormikProvider value={formik}>
            <Row className="me-0 mt-2">
              <Col xs={12} md={6} lg={4}>
                <label className="mb-1">Date</label>
                <div className="d-flex">
                  <Field
                    name="FromDate"
                    placeholder={"From Date"}
                    component={CustomFlatpickr}
                    options={{
                      altInput: true,
                      altFormat: "j-M-Y",
                      dateFormat: "d-m-Y",
                    }}
                  />
                  <div>&nbsp;</div>
                  <Field
                    name="ToDate"
                    placeholder={"To Date"}
                    component={CustomFlatpickr}
                    options={{
                      altInput: true,
                      altFormat: "j-M-Y",
                      dateFormat: "d-m-Y",
                      minDate: FromDate,
                    }}
                  />
                </div>
              </Col>
            </Row>
          </FormikProvider>

          <Spin spinning={summaryLoading} tip="Loading...">
            <div className="rdd-section-title">
              Logged by me ({fromDate === toDate ? displayDate(FromDate) : `${displayDate(FromDate)} to ${displayDate(ToDate)}`})
            </div>
            <div className="rdd-cards">{PERIOD_CARDS.map(renderCard)}</div>

            <div className="rdd-section-title">
              Current position of my allocated tests{" "}
              <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom"
                overlay={
                  <Popover id="rdd-position-info">
                    <Popover.Body>
                      Shows your allocated tests from Inward approval until Testing is approved. Approved tests are
                      removed from these counts. Not affected by the date filter.
                    </Popover.Body>
                  </Popover>
                }
              >
                <span className="rdd-info" role="button" tabIndex={0} aria-label="About current position">
                  <i className="fa fa-info-circle" style={{ cursor: "pointer", color: "#9698ab" }}></i>
                </span>
              </OverlayTrigger>
            </div>
            <div className="rdd-cards">{POSITION_CARDS.map(renderCard)}</div>
          </Spin>

          {selectedCard && (
            <Spin spinning={detailLoading} tip="Loading...">
              <div className="rdd-detail mt-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="rdd-section-title mb-1">
                    {selectedCard.label} - {recordCount}
                  </div>
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm me-2"
                      onClick={exportDetail}
                      disabled={detailLoading || recordCount === 0}
                    >
                      <i className="fa-solid fa-file-excel me-2"></i>Export Excel
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={(e) => handleCardClick(selectedType, e)}
                    >
                      <i className="fa-solid fa-xmark me-2"></i>Close
                    </button>
                  </div>
                </div>
                <div className="table-responsive active-projects style-1 shorting">
                  <div className="dataTables_wrapper no-footer">
                    <table className="table dataTable no-footer mb-0">
                      <thead>
                        <tr>
                          {columns.map((c) => (
                            <th key={c.title} style={{ textAlign: "left", width: c.width }}>
                              {c.title}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detailRows.length > 0 ? (
                          detailRows.map((row, i) => (
                            <tr key={i}>
                              {columns.map((c) => (
                                <td
                                  key={c.title}
                                  className={c.bold ? "fw-bold" : undefined}
                                  style={{ textAlign: "left", verticalAlign: "middle" }}
                                >
                                  {c.render(row)}
                                </td>
                              ))}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={columns.length} className="text-center text-muted">
                              {detailLoading ? "" : "No record found."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <div className="d-sm-flex text-center justify-content-between align-items-center">
                      <CustomSizePerPage
                        getData={getData}
                        currentSizePerPage={currentSizePerPage}
                      />
                      <div className="dataTables_info">
                        Showing{" "}
                        {recordCount > 0 ? (currentPage - 1) * currentSizePerPage + 1 : 0}{" "}
                        to{" "}
                        {recordCount > currentPage * currentSizePerPage
                          ? currentPage * currentSizePerPage
                          : recordCount}{" "}
                        of {recordCount} entries
                      </div>
                      <CustomPaginationButtons
                        totalPages={Math.ceil(recordCount / currentSizePerPage)}
                        currentPage={currentPage}
                        currentSizePerPage={currentSizePerPage}
                        getData={getData}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Spin>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestingRawDataDashboard;
